import type { Observation, Procedure, Quantity } from "@medplum/fhirtypes";
import {
  ORDER_PARAMETER_VALUE_URL,
  TREATMENT_CHOICE_CODE_SYSTEM,
  TREATMENT_OBSERVATION_COMPONENT_CODE_SYSTEM,
  TREATMENT_OBSERVATION_DEFINITION_CODE_SYSTEM,
  TREATMENT_OBSERVATION_ID_SYSTEM,
  TREATMENT_OBSERVATION_KIND,
  TREATMENT_PROCEDURE_KIND_SYSTEM,
  TREATMENT_TRIAL_ID_SYSTEM,
  TREATMENT_TRIAL_KIND,
  TREATMENT_TRIAL_SOURCE_URL,
  TREATMENT_UNCAPTURED_ID_SYSTEM,
  TREATMENT_UNCAPTURED_KIND,
} from "@repo/api";
import type { ProcedurePerformedPeriod as PerformedPeriod } from "@repo/domain";
import {
  type AnsweredValue,
  asMemberId,
  asObservationId,
  asPatientDocumentId,
  asPlannedTrialId,
  type ProcedureTrial,
  type UnitValue,
} from "@repo/domain";
import { TRIAL_PLANNED_TRIAL_URL } from "@repo/fhir-systems/extensions";
import {
  PATIENT_DOCUMENT_ENTITY_ID_SYSTEM,
  PRACTITIONER_ENTITY_ID_SYSTEM,
  TREATMENT_PROCEDURE_ENTITY_ID_SYSTEM,
} from "@repo/fhir-systems/identifiers";
import { procedureStatusOf } from "../lib/procedure-lifecycle";
import { readProcedurePeriod } from "./procedure-period";

/**
 * Where a charted treatment's `Procedure` + `Observation` graph keeps what the
 * order-set projection reads (ADR-0021: views read FHIR rows directly and never
 * import codecs).
 *
 * ONLY WHAT THE PROJECTION READS, which is why the order link, the patient, the
 * visit and the pinned definition are absent: the card shows none of them, and
 * reading a field nothing uses only invents a way for a renderable row to fail.
 *
 * ALWAYS `component[]`, even for a single measurement — the treatment model's
 * rule is that a measurement is a list of one, and a list cannot lie about its
 * length. That is the one place this differs from the diagnostic side, which
 * has two spellings.
 *
 * A row whose status, identity or stored value this module cannot read THROWS:
 * a substituted value is indistinguishable from a real one.
 */

export type TreatmentProcedureRowStatus =
  | "inProgress"
  | "performed"
  | "unableToComplete"
  | "didNotPerform"
  | "enteredInError";

export interface TreatmentProcedureObservationRow {
  trialId?: string;
  observationId: string;
  definitionId: string;
  observedAt: string;
  components: Array<{ componentId: string; value: AnsweredValue }>;
  note?: string;
  lifecycle: { status: "Final" } | { status: "Corrected" } | { status: "EnteredInError" };
}

export interface TreatmentProcedureRow {
  id: string;
  status: TreatmentProcedureRowStatus;
  performerIds: string[];
  performedPeriod?: PerformedPeriod;
  note?: string;
  trials: ProcedureTrial[];
  observations: TreatmentProcedureObservationRow[];
  /** Declaration ids that were attempted and could not be captured. */
  uncaptured: string[];
}

/**
 * ONE-TO-ONE. Every domain state has an exact FHIR event status, so there is
 * nothing to collapse and nothing to infer; a status outside this table is an
 * unreadable row rather than "in progress".
 */

// Deliberately not `Procedure/<id>`: that spelling is a FHIR reference, and this
// is a row locator in a message about a row that could not be read.
function unreadable(kind: string, id: string | undefined, field: string, value?: unknown): never {
  throw new Error(
    `Treatment ${kind} row '${id ?? "<unassigned>"}' has no readable ${field}${
      value === undefined ? "" : ` (${JSON.stringify(value)})`
    }`,
  );
}

function kindOf(resource: { meta?: { tag?: { system?: string; code?: string }[] } }): string | undefined {
  return resource.meta?.tag?.find((tag) => tag.system === TREATMENT_PROCEDURE_KIND_SYSTEM)?.code;
}

function identityOf(row: Observation, system: string): string | undefined {
  return row.identifier?.find((identifier) => identifier.system === system)?.value;
}

function unitFrom(quantity: Quantity | undefined): UnitValue | undefined {
  if (!quantity?.code) return undefined;
  return { code: quantity.code, display: quantity.unit ?? quantity.code };
}

type ValueSlots = Pick<
  Observation,
  "valueString" | "valueQuantity" | "valueInteger" | "valueBoolean" | "valueCodeableConcept"
>;

/**
 * The stored `value[x]` slot decides the kind, and a component holding none of
 * them is a malformed row rather than empty text. Text is read last because it
 * is the only kind whose absence is indistinguishable from a blank answer.
 */
function readValue(row: Observation, source: ValueSlots): AnsweredValue {
  if (source.valueQuantity?.value !== undefined) {
    const unit = unitFrom(source.valueQuantity);
    return { kind: "Quantity", value: source.valueQuantity.value, ...(unit === undefined ? {} : { unit }) };
  }
  if (source.valueInteger !== undefined) return { kind: "Integer", value: source.valueInteger };
  if (source.valueBoolean !== undefined) return { kind: "Boolean", value: source.valueBoolean };
  if (source.valueCodeableConcept) {
    return {
      kind: "Choice",
      selected: (source.valueCodeableConcept.coding ?? []).flatMap((coding) =>
        coding.system === TREATMENT_CHOICE_CODE_SYSTEM && coding.code ? [asMemberId(coding.code)] : [],
      ),
      ...(source.valueCodeableConcept.text === undefined ? {} : { freeText: source.valueCodeableConcept.text }),
    };
  }
  if (source.valueString === undefined) unreadable("observation", row.id, "component value");
  return { kind: "Text", value: source.valueString };
}

function readObservation(row: Observation): TreatmentProcedureObservationRow {
  const note = row.note?.[0]?.text;
  const trialId = row.derivedFrom?.find((reference) => reference.identifier?.system === TREATMENT_TRIAL_ID_SYSTEM)
    ?.identifier?.value;
  return {
    ...(trialId ? { trialId } : {}),
    observationId:
      identityOf(row, TREATMENT_OBSERVATION_ID_SYSTEM) ?? unreadable("observation", row.id, "measurement identity"),
    definitionId:
      row.code?.coding?.find((coding) => coding.system === TREATMENT_OBSERVATION_DEFINITION_CODE_SYSTEM)?.code ??
      unreadable("observation", row.id, "declaration identity"),
    observedAt: row.effectiveDateTime ?? unreadable("observation", row.id, "observed instant"),
    components: (row.component ?? []).map((component) => ({
      componentId:
        component.code?.coding?.find((coding) => coding.system === TREATMENT_OBSERVATION_COMPONENT_CODE_SYSTEM)?.code ??
        unreadable("observation", row.id, "component identity"),
      value: readValue(row, component),
    })),
    ...(note === undefined ? {} : { note }),
    lifecycle: {
      status: row.status === "entered-in-error" ? "EnteredInError" : row.status === "corrected" ? "Corrected" : "Final",
    },
  };
}

export function readProcedureTrial(
  row: Observation,
  identitySystem = TREATMENT_TRIAL_ID_SYSTEM,
  sourceUrl = TREATMENT_TRIAL_SOURCE_URL,
): ProcedureTrial {
  const planned = row.extension?.find((entry) => entry.url === TRIAL_PLANNED_TRIAL_URL)?.valueString;
  const document = row.derivedFrom?.find((ref) => ref.identifier?.system === PATIENT_DOCUMENT_ENTITY_ID_SYSTEM);
  return {
    ...(planned ? { plannedTrialId: asPlannedTrialId(planned) } : {}),
    ...(document
      ? {
          derivedFromDocumentId: asPatientDocumentId(
            document.identifier?.value ?? unreadable("trial", row.id, "document identity"),
          ),
        }
      : {}),
    id: asObservationId(identityOf(row, identitySystem) ?? unreadable("trial", row.id, "identity")),
    effectiveDateTime: row.effectiveDateTime ?? unreadable("trial", row.id, "clinical time"),
    lifecycle: {
      status:
        row.status === "entered-in-error"
          ? "EnteredInError"
          : row.status === "corrected"
            ? "Corrected"
            : row.status === "final"
              ? "Final"
              : unreadable("trial", row.id, "status", row.status),
    },
    values: (row.component ?? []).map((component) => {
      const identity = component.extension?.find((entry) => entry.url === ORDER_PARAMETER_VALUE_URL)?.extension;
      const namespace =
        identity?.find((entry) => entry.url === "source")?.valueString ??
        unreadable("trial", row.id, "parameter namespace");
      const id =
        identity?.find((entry) => entry.url === "parameterId")?.valueString ??
        unreadable("trial", row.id, "parameter identity");
      const source = component.extension?.find((entry) => entry.url === sourceUrl)?.valueCode;
      if (source !== "ordered" && source !== "entered") unreadable("trial", row.id, "provenance", source);
      return {
        source,
        answer: {
          source: namespace === "self" ? "self" : asMemberId(namespace),
          parameterId: asMemberId(id),
          value: readValue(row, component),
        },
      };
    }),
  };
}

export function readTreatmentProcedure(anchor: Procedure, children: readonly Observation[]): TreatmentProcedureRow {
  const identity = anchor.identifier?.find(
    (identifier) => identifier.system === TREATMENT_PROCEDURE_ENTITY_ID_SYSTEM,
  )?.value;
  const note = anchor.note?.[0]?.text;
  const period = anchor.performedPeriod;
  return {
    id: identity ?? unreadable("procedure", anchor.id, "procedure identity"),
    status: procedureStatusOf(anchor.status) ?? unreadable("procedure", anchor.id, "status", anchor.status),
    performerIds: (anchor.performer ?? []).flatMap((performer) =>
      performer.actor?.identifier?.system === PRACTITIONER_ENTITY_ID_SYSTEM && performer.actor.identifier.value
        ? [performer.actor.identifier.value]
        : [],
    ),
    ...(period?.start === undefined ? {} : { performedPeriod: readProcedurePeriod(period) }),
    ...(note === undefined ? {} : { note }),
    trials: children
      .filter((row) => kindOf(row) === TREATMENT_TRIAL_KIND)
      .map((row) => readProcedureTrial(row))
      .sort((a, b) => Date.parse(a.effectiveDateTime) - Date.parse(b.effectiveDateTime)),
    observations: children.filter((row) => kindOf(row) === TREATMENT_OBSERVATION_KIND).map(readObservation),
    // A struck uncaptured slot asserts nothing, so the declaration reads as open
    // again rather than as one somebody failed to capture. A row written before
    // the slot carried its own identifier still names its declaration in `code`.
    uncaptured: children
      .filter((row) => kindOf(row) === TREATMENT_UNCAPTURED_KIND && row.status !== "entered-in-error")
      .map(
        (row) =>
          identityOf(row, TREATMENT_UNCAPTURED_ID_SYSTEM) ||
          row.code?.coding?.find((coding) => coding.system === TREATMENT_OBSERVATION_DEFINITION_CODE_SYSTEM)?.code ||
          unreadable("uncaptured observation", row.id, "declaration identity"),
      ),
  };
}
